import { useState } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Check, Loader2, Disc3 } from 'lucide-react';
import { toast } from 'sonner';

// Parse URL to determine embed type
function getEmbedInfo(url) {
    try {
        const u = new URL(url);
        
        // Spotify
        if (u.hostname.includes('spotify.com')) {
            const match = url.match(/\/(track|album|playlist|artist)\/([a-zA-Z0-9]+)/);
            if (match) {
                return {
                    type: 'spotify',
                    embedUrl: `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=generator&theme=0`,
                    canEmbed: true
                };
            }
        }
        
        // YouTube
        if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
            let videoId = null;
            if (u.hostname.includes('youtu.be')) {
                videoId = u.pathname.slice(1);
            } else {
                videoId = u.searchParams.get('v');
            }
            if (videoId) {
                return {
                    type: 'youtube',
                    embedUrl: `https://www.youtube.com/embed/${videoId}`,
                    canEmbed: true
                };
            }
        }
        
        // SoundCloud (basic embed)
        if (u.hostname.includes('soundcloud.com')) {
            return {
                type: 'soundcloud',
                embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23a67c52&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false`,
                canEmbed: true
            };
        }
        
        // Bandcamp
        if (u.hostname.includes('bandcamp.com')) {
            return { type: 'bandcamp', canEmbed: false };
        }
        
        return { type: 'link', canEmbed: false };
    } catch {
        return { type: 'link', canEmbed: false };
    }
}

export default function InspirationEditor({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [url, setUrl] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const embedInfo = url ? getEmbedInfo(url.startsWith('http') ? url : `https://${url}`) : null;

    const handleSave = async () => {
        if (!url.trim()) {
            toast.error('Please enter a URL');
            return;
        }

        let finalUrl = url.trim();
        if (!finalUrl.startsWith('http')) {
            finalUrl = 'https://' + finalUrl;
        }

        const info = getEmbedInfo(finalUrl);

        setSaving(true);
        try {
            const fragment = await addFragment(songId, {
                type: 'inspiration',
                label: 'Reference',
                url: finalUrl,
                embedUrl: info.embedUrl || null,
                embedType: info.type,
                canEmbed: info.canEmbed,
                note: note.trim() || null
            });

            toast.success('Reference saved');
            onComplete(fragment?.id);
        } catch (error) {
            console.error('Error saving inspiration:', error);
            toast.error('Failed to save reference');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4 max-w-lg mx-auto">
            <div className="p-4 rounded bg-muted/40 text-center">
                <Disc3 className="w-6 h-6 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">
                    Add a Spotify, YouTube, or SoundCloud link for reference
                </p>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="url" className="text-xs font-medium">URL</Label>
                <Input
                    id="url"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://open.spotify.com/track/..."
                    className="h-9"
                    autoFocus
                />
                {embedInfo && (
                    <p className="text-xs text-muted-foreground">
                        {embedInfo.canEmbed 
                            ? `✓ ${embedInfo.type} embed supported`
                            : `Will display as link`
                        }
                    </p>
                )}
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="note" className="text-xs font-medium">Note (optional)</Label>
                <Textarea
                    id="note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="drum feel, vocal phrasing, chord voicing..."
                    className="min-h-[60px] resize-none text-sm"
                />
            </div>

            <Button
                onClick={handleSave}
                disabled={saving || !url.trim()}
                className="w-full h-10"
            >
                {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Check className="w-4 h-4 mr-2" />
                )}
                Save Reference
            </Button>
        </div>
    );
}